import { useApolloClient } from '@apollo/client';
import {
  BottomSheetModal,
  BottomSheetModalProvider,
} from '@gorhom/bottom-sheet';
import { useRoute } from '@react-navigation/native';
import {
  createStackNavigator,
  StackScreenProps,
} from '@react-navigation/stack';
import { Formik, FormikProps } from 'formik';
import { TFunction } from 'i18next';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, DeviceEventEmitter, Platform } from 'react-native';
import { useToast } from 'react-native-toast-notifications';
import { InferType } from 'yup';

import { showSubmitErrorToast } from '@components/CustomToast/showSubmitErrorToast';
import { TaskCreatedToast } from '@components/CustomToast/TaskCreatedToast';
import { DateSelect } from '@components/FormModals/DateSelect';
import { DependencySelect } from '@components/FormModals/DependencySelect';
import ProjectSelect from '@components/FormModals/ProjectSelect';
import { SkillSelect } from '@components/FormModals/SkillSelect';
import SecondaryHeader from '@components/Headers/SecondaryHeader';
import { SimpleHeader } from '@components/Headers/SimpleHeader';
import { ConfirmModal } from '@components/Modals/ConfirmModal';
import { TaskEditDatesModal } from '@components/Modals/TaskEditDatesModal';
import { TeamPaywallModal } from '@components/Modals/TeamPaywallModal';
import { MenusProvider } from '@components/ProjectTaskBottomSheet/MenusContext';
import { Box } from '@components/Restyle';
import {
  createTaskDroppingHiddenDependencies,
  dependencyNoticePlacement,
  isDependencyNotFoundError,
} from '@components/Tasks/copyTaskDependencies';
import { buildCreateTaskVariables } from '@components/Tasks/createTaskVariables';
import { EditTaskDates } from '@components/Tasks/EditTaskDates.web';
import { EditTaskDatesBottomSheet } from '@components/Tasks/EditTaskDatesBottomSheet';
import { TaskCreateBottomSheet } from '@components/Tasks/TaskCreateBottomSheet';
import { TaskForm } from '@components/Tasks/TaskForm';
import {
  getTaskFormFieldErrors,
  isTaskFormErrorFullyMapped,
} from '@components/Tasks/taskFormFieldErrors';
import { createTaskFormSubmitHandler } from '@components/Tasks/taskFormSubmitHandler';
import { TASK_LIST_REFETCH_QUERY_NAMES } from '@components/Tasks/taskRefetchQueries';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import {
  DocumentAttributes,
  GetProjectDocument,
  ListMessagesDocument,
  LocalFile,
  TaskChecklistAttributes,
  TaskMemberAttributes,
  useAssignTaskUsersMutation,
  useCreateTaskMutation,
  useDuplicateTaskFilesMutation,
  useUpdateTaskMutation,
} from '@graphql/generated';
import { useAssignTaskNudge } from '@hooks/useAssignTaskNudge';
import useMe from '@hooks/useMe';
import { OwnerNudgeStep, useOwnerNudge } from '@hooks/useOwnerNudge';
import { useProjectTutorial } from '@hooks/useProjectTutorial';
import { useProjectTutorialWeb } from '@hooks/useProjectTutorialWeb';
import useSelectedMembers from '@hooks/useSelectedMembers';
import useTagInput from '@hooks/useTagInput';
import useTask from '@hooks/useTask';
import { useTaskNudge } from '@hooks/useTaskNudge';
import { ChatsStackScreenProps } from '@navigation/chats/chats-stack';
import { TasksStackScreenProps } from '@navigation/tasks/tasks-stack';
import { useAppNavigation } from '@navigation/useAppNavigation';
import createTaskFormSchema from '@schemas/createTaskFormSchema';
import { AddEditTaskMembersFromModal } from '@screens/Members/AddEditTaskMembersFromModal';
import { TaskCopySearch } from '@screens/Tasks/TaskCopySearch';
import { TaskCopySelect } from '@screens/Tasks/TaskCopySelect.web';
import { TaskDetailEditPriority } from '@screens/Tasks/TaskDetailEditPriority';
import { TaskDetailEditStatus } from '@screens/Tasks/TaskDetailEditStatus';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useMixpanel } from '@src/utils/mixpanel';
import { logEvent } from '@utils/eventLogger';
import { beginningOfDayUTC } from '@utils/formatters/date';
import { getSubmitErrorMessage } from '@utils/getSubmitErrorMessage';
import { mutationFailed, toMutationError } from '@utils/graphqlError';
import { runSubmitPreflight } from '@utils/runSubmitPreflight';
import {
  buildTaskAssignedStartConversationPending,
  START_CONVERSATION_PENDING_KEY,
} from '@utils/startConversationTutorial';
import { AsyncStorage } from '@utils/storage';
import { defaultTaskRole, defaultAssignMemberTaskRole } from '@utils/tasks';
import {
  AssignTaskSourceCard,
  sourceCardAssignVariables,
} from '@utils/tasks/assignTaskSourceCard';
import { shouldShowPaywallForProject } from '@utils/teams/paywall';

export type FormValues = InferType<typeof createTaskFormSchema>;

export type TaskFormStackParamList = {
  'task-form': undefined;
  'member-select': undefined;
  'project-select': { isEditTask: false };
  'skill-select': undefined;
  'phase-select': undefined;
  'dependency-select': undefined;
  'task-copy-search': undefined;
  'task-copy-select': undefined;
  'add-edit-task-modal-members': undefined;
  'date-select': { field: FormValues['startDate' | 'dueDate'] };
  'edit-date-select': {
    submitOnSelected: false;
    fieldName: FormValues['startDate' | 'dueDate'];
  };
  'date-select-modal': undefined;
};

export type TaskFormStackScreenProps<T extends keyof TaskFormStackParamList> =
  StackScreenProps<TaskFormStackParamList, T>;

const Stack = createStackNavigator<TaskFormStackParamList>();

const createTaskFormHeader = (t: TFunction) => () => (
  <Box paddingTop='xs'>
    <SecondaryHeader title={t('models:tasks.edit.title')} searchable={false} />
  </Box>
);

const AddMembersHeader = () => <SimpleHeader title='Add Members' />;

const TaskFormStack: React.FC<{
  taskId?: string;
  chatId?: string;
  initialValues: FormValues;
  isShortModal?: boolean;
  editField?: string;
  isAssignTask?: boolean;
  isCopyTask?: boolean;
  screenName?: string;
  openModal?: boolean;
  selectedList?: [];
  checkList?: TaskChecklistAttributes[];
  // TA-10949 #24: threaded down to TaskForm — see its own doc comment.
  attemptCloseRef?: React.MutableRefObject<(() => void) | null>;
  // The MEMBER JOINED card an assign-to-chat flow came from (TA-11038). Only
  // set by TaskModal once the chat is known to be a DM.
  sourceCard?: AssignTaskSourceCard;
}> = ({
  taskId,
  chatId,
  initialValues,
  isShortModal,
  editField,
  isAssignTask,
  isCopyTask,
  screenName,
  openModal = false,
  selectedList = [],
  checkList = [],
  attemptCloseRef,
  sourceCard,
}) => {
  const { t } = useTranslation();
  const navigation = useAppNavigation();
  const { navigateToTask } = navigation;
  const { setCurrentTask, copyItems } = useTask();
  const { setSelectedMembers, selectedMembers } = useSelectedMembers();
  const { params } = useRoute<
    ChatsStackScreenProps<'task-form-modal'>['route'] &
      TasksStackScreenProps<'task-create'>['route']
  >();
  const [errorMsg, setErrorMsg] = useState('');
  // Web surfaces a failed submit as an error toast (same StatusToast as the
  // create-project form) rather than the ConfirmModal below, which the user
  // has to dismiss before they can retry. Native keeps the modal.
  const showSubmitError = useCallback(
    (error: unknown) => {
      const message = getSubmitErrorMessage(error, t);
      if (Platform.OS === 'web') {
        showSubmitErrorToast(message);
        return;
      }
      setErrorMsg(message);
    },
    [t]
  );
  const [copyTask, setCopyTask] = useState();
  // CreateTask and the web task panel pass isCopyTask for plain creates too;
  // a real copy also has the source task's id.
  const isCopyingTask = !!isCopyTask && !!taskId;
  // TA-11209: set just before a copy is retried without dependencies, and
  // read by the createTaskData effect below, which puts the notice into the
  // "Task created" toast. Setting it before the retry is sent (not after it
  // resolves) means it is always in place when the retry's data arrives.
  const dependenciesDroppedRef = useRef(false);
  const { tags, setTags } = useTagInput();
  const tagsCollection = tags(chatId);
  const { me } = useMe();
  const [assignMembers, setAssignMembers] = useState<TaskMemberAttributes[]>(
    []
  );
  const { trackEvent } = useMixpanel();

  const includeFiles = openModal ? selectedList[2]?.list[0].isSelected : false;
  const getEditPage = () => {
    switch (editField) {
      case 'status':
        return <TaskDetailEditStatus />;
      case 'priority':
        return <TaskDetailEditPriority />;
      case 'dates':
        return <TaskEditDatesModal />;
    }
  };

  const { setIdTaskDetailOpen, setPaywallModalOpen, setPaywallTeam } =
    useWebDrawer();
  const client = useApolloClient();
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallProject, setPaywallProject] = useState<any>(null);

  const bottomSheetTaskFormRef = useRef<BottomSheetModal>(null);

  const { projectId, projectUUId, goBackOnComplete, taskname } = params || {
    projectId: '',
    projectUUId: '',
    goBackOnComplete: false,
  };

  useEffect(() => {
    if (Platform.OS === 'web') {
      const unsubscribe = navigation
        .getParent()
        ?.addListener('drawerItemPress', (e) => {
          navigation.reset({ routes: [{ name: 'tasks' }], index: 0 });
          e.preventDefault();
        });
      return unsubscribe;
    }
  }, [navigation]);

  const [duplicateTaskFiles, { data: duplicateTaskFilesData }] =
    useDuplicateTaskFilesMutation({
      refetchQueries: ['listTasks', 'listProjects'],
    });

  useEffect(() => {
    if (!duplicateTaskFilesData) return;
    // NOTE: If errors are returned, inform the user
    navigation.goBack();
    if (isCopyTask && copyTask) {
      setTimeout(() => {
        setCurrentTask(undefined);
        setSelectedMembers([]);
      }, 1000);
    }
  }, [duplicateTaskFilesData]);

  const isGotoTaskDetail = useRef<boolean>(false);

  const [
    assignTaskUsers,
    { data: assignTaskUsersData, error: assignTaskUsersError },
  ] = useAssignTaskUsersMutation({
    refetchQueries: [{ query: ListMessagesDocument, variables: { chatId } }],
    // Code review fix: this component already shows its own generic Alert
    // for any assignTaskUsersError below — opt this call out of the global
    // PERMISSION_DENIED/CONFLICT toast so the user isn't shown both.
    context: { suppressGlobalMutationErrorToast: true },
  });

  useEffect(() => {
    if (!assignTaskUsersData) return;
    setTimeout(() => {
      setSelectedMembers([]);
    }, 1000);
    if (!isGotoTaskDetail.current) {
      (async () => {
        if (Platform.OS !== 'web') {
          // Handoff for MyChatDetail: once back in the chat with the TASK
          // ASSIGNED card, show the delayed one-time "Start a Conversation"
          // tooltip. Best-effort — a storage failure must never block
          // returning to the chat.
          const pending = buildTaskAssignedStartConversationPending({
            members: selectedMembers,
            meId: me?.id,
            chatId,
          });
          if (pending) {
            try {
              await AsyncStorage.setItem(
                START_CONVERSATION_PENDING_KEY,
                JSON.stringify(pending)
              );
            } catch {
              // skip the tutorial handoff, still open the chat
            }
          }
        }
        navigation.navigate('chat-detail-z', {
          id: chatId,
          showKeyboardFocus: true,
        });
      })();
    }
    completeNudgeStep(OwnerNudgeStep.AssignTask);
    trackEvent(MIXPANEL_EVENTS.TASK_ASSIGNED, {
      project_id: projectUUId || projectId,
      task_id: assignTaskUsersData.assignTaskUsers?.id,
      assignee_user_id: selectedMembers.map((m) => m.id),
      assigned_by_user_id: me?.id,
      is_self_assigned: selectedMembers.some((m) => m.id === me?.id),
    });
  }, [assignTaskUsersData]);

  useEffect(() => {
    if (!assignTaskUsersError) return;
    Alert.alert(t('shared:error'), assignTaskUsersError.message);
  }, [assignTaskUsersError]);

  const toast = useToast();
  const { setRecentTaskCreated } = useProjectTutorial();
  const { completeStep: completeNudgeStep } = useOwnerNudge();
  const { completeNudge: completeTaskNudge } = useTaskNudge();
  const { activateNudge: activateAssignTaskNudge } = useAssignTaskNudge();
  const isOnboardingTaskRef = useRef(false);
  // TA-10140: bridges the Formik bag into useCreateTaskMutation's onError
  // (declared before the <Formik> render prop below) so backend
  // field_errors can be mapped onto the corresponding inputs.
  const formikRef = useRef<FormikProps<FormValues>>(null);
  const [createTask, { data: createTaskData, error: createTaskError }] =
    useCreateTaskMutation({
      onError: (error) => {
        Object.entries(getTaskFormFieldErrors(error)).forEach(
          ([field, message]) =>
            formikRef.current?.setFieldError(field as keyof FormValues, message)
        );
        // Field-error mapping above is the only thing this callback does —
        // the generic fallback for unmapped/non-field errors is handled by
        // the createTaskError effect below (see isTaskFormErrorFullyMapped
        // there), so this doesn't also pop its own Alert on top of that
        // ConfirmModal (PR #5782 review fix — this used to double-fire).
      },
      // Code review fix: createTaskError (above) already drives the
      // errorMsg banner for ANY error — opt out of the global
      // PERMISSION_DENIED/CONFLICT toast so this doesn't double up.
      context: { suppressGlobalMutationErrorToast: true },
      refetchQueries: [
        ...((projectUUId ?? initialValues?.projectUUId)
          ? [
              {
                query: GetProjectDocument,
                variables: { id: projectUUId ?? initialValues?.projectUUId },
              },
            ]
          : []),
        'listTasks',
        'listProjects',
      ],
    });

  useEffect(() => {
    if (!createTaskData) return;
    const newTask = createTaskData.createTask;
    // Read and clear up front so an early return can't leak it into the
    // next task's toast.
    const dependenciesDropped = dependenciesDroppedRef.current;
    dependenciesDroppedRef.current = false;
    if (isOnboardingTaskRef.current && Platform.OS !== 'web' && isShortModal) {
      isOnboardingTaskRef.current = false;
      bottomSheetTaskFormRef?.current?.dismiss();
      setShowOverlay(false);
      DeviceEventEmitter.emit('taskCreated', {
        project: createTaskData?.createTask?.project,
      });
      return;
    }
    // NOTE: If errors are returned, inform the user
    completeNudgeStep(OwnerNudgeStep.CreateTask);
    completeTaskNudge();
    if (!isAssignTask && selectedMembers.length === 0) {
      activateAssignTaskNudge(newTask.id);
    }
    trackEvent(MIXPANEL_EVENTS.TASK_CREATED, {
      project_id: newTask.projectId,
      due_date: newTask.dueDate,
      platform: Platform.OS,
    });
    if (Platform.OS === 'web') {
      logEvent({ name: 'task_created' });
    }
    setRecentTaskCreated(newTask);
    DeviceEventEmitter.emit('TASK_CREATED');

    isGotoTaskDetail.current = false;

    const showsTaskCreatedToast = Platform.OS !== 'web' && !isVisibleForMobile;
    const dependencyNotice = dependencyNoticePlacement({
      dependenciesDropped,
      showsTaskCreatedToast,
    });
    // Longer when it carries the dependency note, so there's time to read it.
    const taskCreatedToastMs =
      dependencyNotice === 'taskCreatedToast' ? 5000 : 3000;

    if (dependencyNotice === 'separateToast') {
      toast.show(t('models:tasks.copyDependenciesNotCopied'), {
        duration: 5000,
      });
    }

    showsTaskCreatedToast &&
      toast.show(
        <TaskCreatedToast
          taskName={newTask.name}
          message={t('models:customToast.taskCreated')}
          note={
            dependencyNotice === 'taskCreatedToast'
              ? t('models:tasks.copyDependenciesNotCopiedNote')
              : undefined
          }
          duration={taskCreatedToastMs}
          onPress={() => {
            isGotoTaskDetail.current = true;
            if (isAssignTask && Platform.OS !== 'web') {
              navigation.navigate('chat-detail-z', {
                id: chatId,
                showKeyboardFocus: true,
              });
            }
            navigateToTask(newTask);
          }}
        />,
        {
          type: 'custom',
          duration: taskCreatedToastMs,
          placement: 'top',
          animationType: 'slide-in',
          style: {
            backgroundColor: 'transparent',
            shadowColor: 'transparent',
          },
        }
      );

    if (isCopyTask && taskId) {
      if (
        (copyItems && !copyItems.includeFiles) ||
        (openModal && !includeFiles)
      ) {
        setTimeout(() => {
          setSelectedMembers([]);
        }, 1000);
        return;
      }

      // copy task files
      setCopyTask(newTask);
      newTask &&
        duplicateTaskFiles({
          variables: {
            sourceId: taskId,
            destinationId: newTask.id,
          },
        });
      return;
    }

    if (isAssignTask) {
      if (Platform.OS !== 'web') {
        const selectedUserIds = selectedMembers.map((u) => {
          return { userId: u.id, role: defaultAssignMemberTaskRole };
        });
        newTask.id &&
          chatId &&
          assignTaskUsers({
            variables: {
              id: createTaskData.createTask.id,
              members: assignMembers || selectedUserIds,
              chatId: chatId,
              ...sourceCardAssignVariables({
                sourceCard,
                chatId,
                taskProjectId: createTaskData.createTask.projectId,
                // TaskModal only forwards the card for a chat known to be a DM.
                isGroupChat: false,
              }),
            },
          });
      }

      return;
    }

    if (goBackOnComplete) {
      if (Platform.OS !== 'web') {
        const collectionToUpdate = tagsCollection?.findIndex(
          (t) => t.project?.id === createTaskData.createTask.projectId
        );
        if (collectionToUpdate !== undefined && collectionToUpdate !== -1) {
          // put task into correct coll based on project
          if (
            !tagsCollection?.[collectionToUpdate].tasks.find(
              (t) => t.id === createTaskData.createTask.id
            )
          ) {
            const newTagsColl = [...(tagsCollection ?? [])];
            newTagsColl[collectionToUpdate] = {
              ...newTagsColl[collectionToUpdate],
              tasks: [
                ...newTagsColl[collectionToUpdate].tasks,
                createTaskData.createTask,
              ],
            };

            setTags(chatId, newTagsColl);
          }
        } else if (me) {
          // new tags coll
          const newTagsColl = [
            ...(tagsCollection ?? []),
            {
              tasks: [createTaskData.createTask],
              project: createTaskData.createTask.project,
              author: me,
            },
          ];

          setTags(chatId, newTagsColl);
          navigation.navigate('chat-detail-z', {
            id: chatId,
            showKeyboardFocus: true,
          });
        }
      } else {
        navigation.goBack();
      }
      return;
    } else if (Platform.OS === 'web') {
      setIdTaskDetailOpen(newTask.id);
    }
  }, [createTaskData]);

  useEffect(() => {
    if (!createTaskError) return;
    // PR #5782 review fix: this used to fire for EVERY error regardless of
    // whether onError above already mapped inline field errors, so a fully
    // mapped VALIDATION_FAILED response showed both the inline errors and
    // this generic modal. Only skip it when every backend field_errors key
    // was actually mapped onto a Formik field — isTaskFormErrorFullyMapped
    // is false (so the banner still shows) for message-only errors with no
    // field_errors at all, which a previous version of this fix wrongly
    // suppressed too, leaving those errors with zero feedback.
    if (isTaskFormErrorFullyMapped(createTaskError)) return;
    // TA-11209: submit retries this copy without dependencies, so the first
    // attempt's error is not the outcome. A failed retry still shows here.
    if (isCopyingTask && isDependencyNotFoundError(createTaskError)) return;
    showSubmitError(createTaskError);
  }, [createTaskError, showSubmitError, isCopyingTask]);

  const [updateTask, { data: updateTaskData, error: updateTaskError }] =
    useUpdateTaskMutation({
      refetchQueries: [...TASK_LIST_REFETCH_QUERY_NAMES],
      // Code review fix: updateTaskError (below) already drives an
      // unconditional Alert for any error — opt out of the global
      // PERMISSION_DENIED/CONFLICT toast so this doesn't double up.
      context: { suppressGlobalMutationErrorToast: true },
    });

  useEffect(() => {
    if (!updateTaskData) return;
    navigation.goBack();
  }, [updateTaskData]);

  useEffect(() => {
    if (!updateTaskError) return;
    Alert.alert(t('shared:error'), updateTaskError.message);
  }, [updateTaskError]);
  const {
    isVisible,
    setProjectTutorialWebStateAndInStorage,
    projectTutorialWebState,
    setShowOverlay,
  } = useProjectTutorialWeb();
  const { isVisible: isVisibleForMobile } = useProjectTutorial();
  const submit = async (values: FormValues) => {
    const {
      name,
      description,
      fixedOrder,
      frequency,
      recurring,
      dependencyIds,
      projectId,
      startDate,
      dueDate,
      priority,
      status,
      users,
      phaseId,
      skillIds,
      completedAt,
      inviteEmails,
      files,
    } = values;

    const members: TaskMemberAttributes[] = (users || [])
      .filter((mt) => mt.isTaskTagUser !== true)
      .map((u) => {
        return {
          userId: u.id,
          role: u.role || defaultTaskRole,
        };
      });

    const ttMembers = (users || [])
      .filter((mt) => {
        return mt.isTaskTagUser === true;
      })
      .map((u) => {
        return {
          email: u.email,
          role: u.role || defaultTaskRole,
        };
      });

    // Handle file uploads - files are already uploaded when selected
    let fileAttributes: DocumentAttributes[] = [];
    if (files && Array.isArray(files) && files.length > 0) {
      // Files are already uploaded with blobIds, just convert to DocumentAttributes
      const localFiles = files as LocalFile[];
      fileAttributes = localFiles
        .filter((file) => file.blobId && file.blobId !== '') // Only include files that have been uploaded
        .map((file) => {
          return {
            name: file.name,
            contentType: file.contentType,
            isImage: file.isImage || false,
            isAudio: file.isAudio || false,
            blobId: file.blobId || '',
            clientId: file.clientId,
            duration: file.duration || undefined,
          } as DocumentAttributes;
        });
    }

    const attributes = {
      name,
      description,
      fixedOrder,
      frequency,
      dependencyIds,
      recurring,
      projectId,
      phaseId,
      priority,
      status,
      members: isAssignTask ? [] : members,
      skillIds,
      ...{
        startDate: startDate
          ? beginningOfDayUTC(startDate).toISOString()
          : null,
      },
      ...{ dueDate: dueDate ? beginningOfDayUTC(dueDate).toISOString() : null },
      ...(isCopyTask &&
        copyItems?.includeCheckLists && {
          checklists: values.checklists?.map(
            (item: TaskChecklistAttributes) => {
              return { name: item.name, completed: false };
            }
          ),
        }),
      ...(openModal && {
        checklists: checkList?.map((item: TaskChecklistAttributes) => {
          return { name: item.name, completed: false };
        }),
      }),
      ...{
        completedAt: !isCopyTask && !openModal && taskId ? completedAt : null,
      },
      files: fileAttributes.length > 0 ? fileAttributes : undefined,
    };
    if (taskId && !isCopyTask && !openModal) {
      const cacheId = client.cache.identify({
        __typename: 'Task',
        id: taskId,
      });
      if (cacheId) {
        client.cache.modify({
          id: cacheId,
          fields: {
            name() {
              return attributes.name;
            },
            startDate() {
              return attributes.startDate;
            },
            dueDate() {
              return attributes.dueDate;
            },
            status() {
              return attributes.status ?? null;
            },
            priority() {
              return attributes.priority ?? null;
            },
            description() {
              return attributes.description ?? null;
            },
          },
        });
      }
      navigation.goBack();
      await updateTask({
        variables: { id: taskId, attributes },
        onError: () => {
          if (editField === 'priority') {
            toast.show(t('models:tasks.priorityUpdateFailed'), {
              duration: 3000,
            });
          } else if (editField === 'dates') {
            toast.show(t('models:tasks.dateUpdatedFailed'), {
              duration: 3000,
            });
          }
        },
        onCompleted: () => {
          if (editField === 'priority') {
            toast.show(t('models:tasks.priorityUpdated'), {
              duration: 3000,
            });
          } else if (editField === 'dates') {
            toast.show(t('models:tasks.dateUpdated'), {
              duration: 3000,
            });
          }
        },
        // Updating priority or dates can move a task between My Tasks'
        // groups, change its filtered membership, or alter its sort order —
        // refresh every list connection, same set as the hook above.
        refetchQueries: [...TASK_LIST_REFETCH_QUERY_NAMES],
      });
    } else {
      // Check paywall before creating task
      if (projectId) {
        // Use Apollo client to fetch project for paywall check. This runs
        // before createTask, so an offline failure used to abort the submit
        // with no UI at all — runSubmitPreflight shows the error and rethrows
        // (see its docs for why rejecting matters here).
        const { data: projectData } = await runSubmitPreflight(
          () =>
            client.query({
              query: GetProjectDocument,
              variables: { id: projectId },
              fetchPolicy: 'network-only',
            }),
          showSubmitError
        );
        const projectForPaywall = projectData?.getProject;
        if (
          projectForPaywall &&
          shouldShowPaywallForProject(projectForPaywall)
        ) {
          if (Platform.OS === 'web') {
            setPaywallTeam(projectForPaywall?.team ?? undefined);
            setPaywallModalOpen(true);
          } else {
            setPaywallProject(projectForPaywall);
            setShowPaywall(true);
          }
          return;
        }
      }

      if (isAssignTask && chatId) {
        setAssignMembers(members);
      }

      isOnboardingTaskRef.current = !!values.isOnboardingTask;
      dependenciesDroppedRef.current = false;
      const { result: createResult } =
        await createTaskDroppingHiddenDependencies({
          variables: buildCreateTaskVariables({
            attributes,
            taskMembers: [...inviteEmails, ...ttMembers],
            isAssignTask,
            chatId,
            sourceCard,
          }),
          isCopy: isCopyingTask,
          create: (variables) =>
            createTask({
              variables,
              refetchQueries: [
                'listTasks',
                'listActivitiesByProject',
                'listActivities',
              ],
            }),
          onRetryWithoutDependencies: () => {
            dependenciesDroppedRef.current = true;
          },
        });
      // PR #5782 review fix: under errorPolicy: 'all' — and because this
      // mutation supplies onError — this resolves instead of rejecting even
      // when the task was never created, so everything below used to run as
      // if it had been. `mutationFailed` covers both shapes: a
      // `GraphQLError[]` for business errors and an `ApolloError` object for
      // network-level failures.
      if (mutationFailed(createResult)) {
        // The message is already on screen (onError + the createTaskError
        // effect above). Reject so submit callers skip their success-only
        // cleanup: runTaskCreateSubmit's onSuccess resets the form and clears
        // the attachments, which would throw away the user's input for a task
        // that was never created (TA-10210).
        throw toMutationError(createResult.errors, 'createTask failed');
      }
      if (isVisible) {
        setProjectTutorialWebStateAndInStorage({
          ...projectTutorialWebState,
          TASK: 'DONE',
        });
        setShowOverlay(false);
      }
    }
  };

  return (
    <>
      {errorMsg !== '' && !isShortModal && (
        <ConfirmModal
          isAlert={true}
          showModal={errorMsg != ''}
          onClose={() => setErrorMsg('')}
          onPress={() => setErrorMsg('')}
          title={t('shared:error')}
          buttonText='    OK    '
          message={errorMsg}
        />
      )}
      {Platform.OS !== 'web' && (
        <TeamPaywallModal
          visible={showPaywall}
          onClose={() => setShowPaywall(false)}
          team={paywallProject?.team}
          project={paywallProject}
        />
      )}
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        onSubmit={createTaskFormSubmitHandler(submit)}
        validationSchema={createTaskFormSchema}>
        {({ values }) => {
          if (isShortModal) {
            if (taskId && !isCopyTask && !openModal) {
              if (editField === 'dates') {
                if (Platform.OS === 'web') {
                  return <EditTaskDates />;
                } else {
                  return getEditPage();
                }
              } else {
                return getEditPage();
              }
            } else {
              return (
                <MenusProvider>
                  <BottomSheetModalProvider>
                    <TaskCreateBottomSheet
                      parentProject={!!projectId || !!projectUUId}
                      ref={bottomSheetTaskFormRef}
                      taskname={taskname}
                      selectedList={selectedList}
                      isCopyTask={isCopyTask}
                      values={values}
                      goBackOnComplete={goBackOnComplete || false}
                      chatId={chatId}
                      isAssignTask={isAssignTask}
                      errorMsg={errorMsg}
                      onDismissError={() => setErrorMsg('')}
                    />
                  </BottomSheetModalProvider>
                </MenusProvider>
              );
            }
          }

          return (
            <Stack.Navigator
              initialRouteName='task-form'
              screenOptions={{
                headerShown: Platform.OS !== 'web',
              }}>
              <Stack.Screen
                name='task-form'
                options={() => ({
                  detachPreviousScreen: false,
                  header: createTaskFormHeader(t),
                })}>
                {() => (
                  <TaskForm
                    parentProject={!!projectId}
                    screenName={screenName}
                    attemptCloseRef={attemptCloseRef}
                  />
                )}
              </Stack.Screen>
              <Stack.Group
                screenOptions={{
                  headerShown: false,
                  presentation: 'transparentModal',

                  cardOverlayEnabled: Platform.OS !== 'web',
                  cardStyleInterpolator: ({ current: { progress } }) => ({
                    containerStyle: {
                      opacity: progress.interpolate({
                        inputRange: [0, 1],
                        outputRange: [0, 1],
                        extrapolate: 'clamp',
                      }),
                    },
                    overlayStyle: {
                      opacity: progress.interpolate({
                        inputRange: [0, 1],
                        outputRange: [0, 0.6],
                        extrapolate: 'clamp',
                      }),
                    },
                  }),
                }}>
                <Stack.Screen
                  name='add-edit-task-modal-members'
                  component={AddEditTaskMembersFromModal}
                  options={{
                    header: AddMembersHeader,
                  }}
                />
                <Stack.Screen
                  name='project-select'
                  component={ProjectSelect}
                  options={{ title: t('shared:projects') }}
                />
                <Stack.Screen
                  name='skill-select'
                  component={SkillSelect}
                  options={{ title: t('shared:skills') }}
                />
                <Stack.Screen
                  name='dependency-select'
                  component={DependencySelect}
                  options={{ title: t('shared:dependencies') }}
                />
                <Stack.Screen name='date-select' component={DateSelect} />
                <Stack.Screen name='edit-date-select'>
                  {() => (
                    <BottomSheetModalProvider>
                      <EditTaskDatesBottomSheet ref={bottomSheetTaskFormRef} />
                    </BottomSheetModalProvider>
                  )}
                </Stack.Screen>
              </Stack.Group>

              <Stack.Group
                screenOptions={{
                  headerShown: false,
                  presentation: 'transparentModal',
                }}>
                <Stack.Screen
                  name='task-copy-search'
                  component={TaskCopySearch}
                  options={{ title: t('shared:copy') }}
                />
                <Stack.Screen
                  name='task-copy-select'
                  component={TaskCopySelect}
                  options={{ title: t('shared:select') }}
                />
              </Stack.Group>
            </Stack.Navigator>
          );
        }}
      </Formik>
    </>
  );
};

export default TaskFormStack;
